import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export type MovementType = 'transfer' | 'promotion' | 'demotion' | 'salary_revision';

export type MovementStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'scheduled'
  | 'applied'
  | 'failed'
  | 'blocked';

export type ApprovalStepRole = 'manager' | 'hr' | 'admin';
export type ApprovalStepStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'skipped'
  | 'bypassed';

export interface MovementApprovalStep {
  role: ApprovalStepRole;
  approverUserId?: string;
  status: ApprovalStepStatus;
  decidedAt?: string;
  decidedBy?: string;
  comment?: string;
  reason?: string;
}

export interface MovementAssignmentSnapshot {
  departmentId?: string | null;
  teamId?: string | null;
  positionId?: string | null;
  jobTitleId?: string | null;
  gradeId?: string | null;
  employmentTypeId?: string | null;
  reportingManagerUserId?: string | null;
  workLocation?: string | null;
}

export type CompensationType = 'salaried' | 'hourly';

/** Pay terms carried by a salary revision. Stripped server-side without `hr:directory`. */
export interface MovementCompensation {
  compensationType: CompensationType;
  baseSalary?: number;
  hourlyRate?: number;
  currency?: string;
}

export interface MovementPayrollImpact {
  checkedAt: string;
  affectedPeriods: string[];
  touchesFinalizedPeriod: boolean;
  payrollAdjustmentId?: string;
}

export interface Movement {
  _id: string;
  organizationId: string;
  companyId: string;
  userId: string;
  employmentId: string;
  type: MovementType;
  status: MovementStatus;
  initiatedBy: string;
  effectiveFrom: string;
  requestedAt: string;
  before?: MovementAssignmentSnapshot;
  after: MovementAssignmentSnapshot;
  compensationBefore?: MovementCompensation;
  compensationAfter?: MovementCompensation;
  /** Includes `'compensation'` for salary revisions. */
  changedFields: string[];
  approvalChain: MovementApprovalStep[];
  comments: Array<{ authorId: string; body: string; createdAt: string }>;
  backdated: boolean;
  /** Corrects history from before the latest applied assignment. */
  correction?: boolean;
  payrollImpact?: MovementPayrollImpact;
  appliedAssignmentId?: string;
  appliedAt?: string;
  failureReason?: string;
  applyAttempts: number;
  rejectionReason?: string;
  cancellationReason?: string;
  emergencyBypass: boolean;
}

export interface MovementPage {
  data: Movement[];
  total: number;
  page: number;
  limit: number;
}

export async function listMovements(
  companyId: string,
  params: { page?: number; limit?: number; userId?: string; status?: string } = {}
): Promise<MovementPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<MovementPage>>(
    `/companies/${companyId}/hr/movements`,
    { params }
  );
  return data.data;
}

export async function getMovement(companyId: string, movementId: string): Promise<Movement> {
  const { data } = await apiClient.get<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}`
  );
  return data.data;
}

export interface CreateMovementInput {
  type: MovementType;
  userId: string;
  effectiveFrom: string;
  /** Salary revisions send `{}` — only the compensation changes. */
  after: MovementAssignmentSnapshot;
  comment?: string;
  /** Correct history from a date before the latest applied assignment. */
  correction?: boolean;
  /** Required for `salary_revision`, ignored otherwise. */
  compensation?: {
    compensationType: CompensationType;
    baseSalary?: number;
    hourlyRate?: number;
  };
}

export async function createMovement(
  companyId: string,
  input: CreateMovementInput
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements`,
    input
  );
  return data.data;
}

export async function submitMovement(
  companyId: string,
  movementId: string
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}/submit`,
    {}
  );
  return data.data;
}

export async function approveMovement(
  companyId: string,
  movementId: string,
  comment?: string
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}/approve`,
    comment ? { comment } : {}
  );
  return data.data;
}

export async function rejectMovement(
  companyId: string,
  movementId: string,
  reason: string
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}/reject`,
    { reason }
  );
  return data.data;
}

export async function cancelMovement(
  companyId: string,
  movementId: string,
  reason: string
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}/cancel`,
    { reason }
  );
  return data.data;
}

export async function emergencyBypassMovement(
  companyId: string,
  movementId: string,
  reason: string
): Promise<Movement> {
  const { data } = await apiClient.post<ApiResponseWrapper<Movement>>(
    `/companies/${companyId}/hr/movements/${movementId}/emergency-bypass`,
    { reason }
  );
  return data.data;
}
